import { useState, useMemo, useCallback } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
  type PaginationState,
  type SortingState,
} from '@tanstack/react-table';
import { RefreshCw, Search, Eraser, ArrowUpNarrowWide, ArrowDownWideNarrow, ArrowUpDown, HelpCircle, X, Activity, Repeat, Timer, Database } from 'lucide-react';
import { DateOnlyPicker } from '@/shared/components/DateTimePicker.tsx';
import { useProbeLogList } from '../hooks/useProbeLogs.ts';
import { TenantCombobox } from '@/shared/components/TenantCombobox.tsx';
import type { ProbeLogRow, ProbeResultado, ProbeLogListParams } from '@/shared/types/probe-log.ts';

/* ── constants ────────────────────────────────────────── */

type ResultFilter = 'all' | 'UP' | 'DOWN';

const columnHelper = createColumnHelper<ProbeLogRow>();

const fmtDateTime = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'America/Sao_Paulo',
  day: '2-digit',
  month: '2-digit',
  year: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

/* ── Modal: Como funcionam os probe logs ───────────────── */

function ProbeLogHelpModal({ onClose }: { onClose: () => void }) {
  const handleBackdrop = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={handleBackdrop}
    >
      <div className="relative mx-4 w-full max-w-lg rounded-xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400">
              <HelpCircle size={18} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">O que são os Probe Logs?</h3>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-5 px-6 py-5">
          {/* O que é o Probe */}
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800/50 dark:bg-blue-900/20">
            <div className="mb-2.5 flex items-center gap-2">
              <Activity size={16} className="text-blue-600 dark:text-blue-400" />
              <h4 className="text-sm font-semibold text-blue-800 dark:text-blue-300">O que é o Probe?</h4>
            </div>
            <div className="space-y-1.5 text-xs text-blue-700 dark:text-blue-300/90">
              <p>O probe é uma <strong>verificação HTTP automática</strong> executada a cada <strong>1 minuto</strong> em cada tenant cadastrado.</p>
              <p>O endpoint monitorado é: <code className="rounded bg-blue-100 px-1 py-0.5 dark:bg-blue-900/40">slug.woli.com.br/health</code></p>
            </div>
          </div>

          {/* Resultados */}
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
            <h4 className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">Interpretando os resultados</h4>
            <div className="space-y-1.5 text-xs text-gray-600 dark:text-gray-400">
              <p><strong className="text-green-600 dark:text-green-400">UP:</strong> o servidor respondeu com qualquer status HTTP (200, 404, 500... = servidor no ar)</p>
              <p><strong className="text-red-600 dark:text-red-400">DOWN:</strong> timeout, connection refused, falha DNS ou erro de rede</p>
              <p><strong>HTTP Status:</strong> código retornado pelo servidor (ex: 200, 503). Vazio quando DOWN.</p>
              <p><strong>Tempo (ms):</strong> latência da resposta. Vazio quando DOWN.</p>
              <p><strong>Erro:</strong> mensagem técnica da falha (ex: ECONNREFUSED, ETIMEDOUT)</p>
            </div>
          </div>

          {/* Retry e Anti-flap */}
          <div className="rounded-lg border border-orange-200 bg-orange-50 p-4 dark:border-orange-800/50 dark:bg-orange-900/20">
            <div className="mb-2.5 flex items-center gap-2">
              <Repeat size={16} className="text-orange-600 dark:text-orange-400" />
              <h4 className="text-sm font-semibold text-orange-800 dark:text-orange-300">Retry e Anti-Flap</h4>
            </div>
            <div className="space-y-1.5 text-xs text-orange-700 dark:text-orange-300/90">
              <p>O timeout <strong>cresce a cada falha consecutiva</strong>: 10s no 1º check, 30s no 2º e 60s no 3º. A paciência aumenta entre ciclos, não dentro do mesmo check.</p>
              <p>Apenas após <strong>3 checks DOWN consecutivos</strong> um incidente é aberto automaticamente.</p>
              <p className="text-[11px] italic">Um único DOWN isolado não gera incidente nem desconta do SLA — pode ser instabilidade momentânea da rede.</p>
            </div>
          </div>

          {/* Retenção de dados */}
          <div className="rounded-lg border border-purple-200 bg-purple-50 p-4 dark:border-purple-800/50 dark:bg-purple-900/20">
            <div className="mb-2.5 flex items-center gap-2">
              <Database size={16} className="text-purple-600 dark:text-purple-400" />
              <h4 className="text-sm font-semibold text-purple-800 dark:text-purple-300">Retenção de Dados</h4>
            </div>
            <div className="space-y-1.5 text-xs text-purple-700 dark:text-purple-300/90">
              <p>Para otimizar o armazenamento, registros <strong>UP (sucesso)</strong> são mantidos por <strong>72 horas</strong> e depois removidos automaticamente.</p>
              <p>Registros <strong className="text-red-600 dark:text-red-400">DOWN (falha)</strong> são mantidos por <strong>18 meses</strong> — é a evidência usada para apurar o SLA e responder a contestações.</p>
              <p className="text-[11px] italic">Antes de qualquer limpeza, os dados do dia são consolidados em um <strong>resumo diário permanente</strong>. É dele que saem o gráfico de 90 dias e este relatório, então o histórico não se perde com o expurgo.</p>
            </div>
          </div>

          {/* Dica */}
          <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800/50 dark:bg-green-900/20">
            <div className="mb-2 flex items-center gap-2">
              <Timer size={16} className="text-green-600 dark:text-green-400" />
              <h4 className="text-sm font-semibold text-green-800 dark:text-green-300">Dica</h4>
            </div>
            <p className="text-xs text-green-700 dark:text-green-300/90">
              Para uma visão agregada (total de checks, % UP/DOWN, latência média), use o relatório <strong>"Probe Resumido"</strong> na aba Relatórios.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-3 dark:border-gray-700">
          <button
            onClick={onClose}
            className="w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            Entendi
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── component ────────────────────────────────────────── */

export function ProbeLogListPage() {
  // ── State ─────────────────────────────────────────────
  const [showHelp, setShowHelp] = useState(false);
  const [tenantFilter, setTenantFilter] = useState('');
  const [resultFilter, setResultFilter] = useState<ResultFilter>('all');
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 50 });
  const [sorting, setSorting] = useState<SortingState>([{ id: 'checkedAt', desc: true }]);
  const [searched, setSearched] = useState(false);

  // ── Queries ───────────────────────────────────────────
  const params = useMemo(
    () => ({
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      tenantId: tenantFilter || undefined,
      resultado: (resultFilter === 'all' ? undefined : resultFilter) as ProbeResultado | undefined,
      from: fromDate ? fromDate.toISOString() : undefined,
      to: toDate ? toDate.toISOString() : undefined,
      sortBy: (sorting[0]?.id as ProbeLogListParams['sortBy']) || 'checkedAt',
      sortOrder: (sorting[0]?.desc ? 'desc' : 'asc') as ProbeLogListParams['sortOrder'],
    }),
    [pagination.pageIndex, pagination.pageSize, tenantFilter, resultFilter, fromDate, toDate, sorting],
  );

  const { data: response, isLoading } = useProbeLogList(params, searched);

  // ── Handlers ────────────────────────────────────────────
  function handleSearch() {
    setSearched(true);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }

  function handleClear() {
    setTenantFilter('');
    setResultFilter('all');
    setFromDate(null);
    setToDate(null);
    setSorting([{ id: 'checkedAt', desc: true }]);
    setSearched(false);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }

  // ── Columns ───────────────────────────────────────────
  const columns = useMemo(
    () => [
      columnHelper.accessor('tenantNome', {
        header: 'Tenant',
        enableSorting: false,
        cell: (info) => (
          <div>
            <span className="font-medium">{info.getValue()}</span>
            <span className="ml-1.5 text-xs text-gray-400">{info.row.original.tenantSlug}</span>
          </div>
        ),
      }),
      columnHelper.accessor('checkedAt', {
        header: 'Data/Hora (GMT-3)',
        cell: (info) => (
          <span className="text-xs">{fmtDateTime.format(new Date(info.getValue()))}</span>
        ),
      }),
      columnHelper.accessor('resultado', {
        header: 'Resultado',
        cell: (info) => {
          const val = info.getValue();
          return val === 'UP' ? (
            <span className="inline-flex rounded-full px-2 py-0.5 text-xs font-medium" style={{ background: 'var(--color-status-up)', color: '#fff' }}>
              UP
            </span>
          ) : (
            <span className="inline-flex rounded-full px-2 py-0.5 text-xs font-medium" style={{ background: 'var(--color-status-down)', color: '#fff' }}>
              DOWN
            </span>
          );
        },
      }),
      columnHelper.accessor('statusCode', {
        header: 'HTTP Status',
        cell: (info) => {
          const code = info.getValue();
          if (code == null) return <span className="text-gray-400">—</span>;
          return <span className="font-mono text-xs">{code}</span>;
        },
      }),
      columnHelper.accessor('responseTimeMs', {
        header: 'Tempo (ms)',
        cell: (info) => {
          const ms = info.getValue();
          if (ms == null) return <span className="text-gray-400">—</span>;
          return <span className="font-mono text-xs">{ms}</span>;
        },
      }),
      columnHelper.accessor('errorMessage', {
        header: 'Erro',
        enableSorting: false,
        cell: (info) => {
          const msg = info.getValue();
          if (!msg) return <span className="text-gray-400">—</span>;
          return (
            <span className="max-w-[250px] truncate text-xs text-red-600 dark:text-red-400" title={msg}>
              {msg}
            </span>
          );
        },
      }),
    ],
    [],
  );

  // ── Table ─────────────────────────────────────────────
  const table = useReactTable({
    data: response?.data ?? [],
    columns,
    pageCount: response ? Math.ceil(response.total / response.limit) : -1,
    state: { pagination, sorting },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
  });

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold">Probe Logs</h1>
        <p className="mt-0.5 flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
          Histórico detalhado de cada verificação de disponibilidade
          <button
            onClick={() => setShowHelp(true)}
            className="rounded-full p-0.5 text-gray-400 transition-colors hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-blue-900/30 dark:hover:text-blue-400"
            title="O que são os Probe Logs?"
          >
            <HelpCircle size={16} />
          </button>
        </p>
      </div>

      {/* Filtros */}
      <div className="rounded border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-900">
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div className="min-w-[220px]">
            <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Tenant</label>
            <TenantCombobox
              value={tenantFilter}
              onChange={(id) => setTenantFilter(id)}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Resultado</label>
            <select
              value={resultFilter}
              onChange={(e) => { setResultFilter(e.target.value as ResultFilter); }}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
            >
              <option value="all">Todos</option>
              <option value="UP">UP</option>
              <option value="DOWN">DOWN</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">De</label>
            <DateOnlyPicker
              value={fromDate}
              onChange={(d) => setFromDate(d)}
              maxDate={toDate ?? undefined}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Até</label>
            <DateOnlyPicker
              value={toDate}
              onChange={(d) => setToDate(d)}
              minDate={fromDate ?? undefined}
            />
          </div>
        </div>

        {/* Ações: Pesquisar + Limpar */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleSearch}
            className="flex items-center gap-1.5 rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700"
            title="Utilize os filtros para buscar e filtrar os probe logs"
          >
            <Search size={16} />
            Pesquisar
          </button>
          <button
            onClick={handleClear}
            className="flex items-center gap-1.5 rounded-md bg-gray-400 px-4 py-2 text-sm font-medium text-white hover:bg-gray-500"
          >
            <Eraser size={16} />
            Limpar
          </button>
        </div>
      </div>

      {/* Tabela */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
        {!searched ? (
          <div className="flex h-48 flex-col items-center justify-center gap-1 text-gray-400">
            <Search size={24} className="mb-1" />
            <p className="text-sm font-medium">Utilize os filtros acima e clique em Pesquisar</p>
          </div>
        ) : isLoading ? (
          <div className="flex h-48 items-center justify-center">
            <RefreshCw size={24} className="animate-spin text-gray-400" />
          </div>
        ) : response && response.data.length === 0 ? (
          <div className="flex h-48 flex-col items-center justify-center gap-1 text-gray-400">
            <p className="text-sm font-medium">Nenhum registro encontrado para os filtros selecionados.</p>
          </div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr
                  key={hg.id}
                  className="border-b-2 border-gray-200 bg-gray-50 text-xs uppercase text-gray-500 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-400"
                >
                  {hg.headers.map((header) => {
                    const canSort = header.column.getCanSort();
                    return (
                      <th
                        key={header.id}
                        className={`px-4 py-3 ${canSort ? 'cursor-pointer select-none' : ''}`}
                        onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                      >
                        <span className="inline-flex items-center gap-1">
                          {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                          {canSort && (
                            header.column.getIsSorted() === 'asc'
                              ? <ArrowUpNarrowWide size={14} className="text-blue-600 dark:text-blue-400" />
                              : header.column.getIsSorted() === 'desc'
                                ? <ArrowDownWideNarrow size={14} className="text-blue-600 dark:text-blue-400" />
                                : <ArrowUpDown size={14} className="text-gray-300 dark:text-gray-600" />
                          )}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className={`border-b border-gray-100 transition-colors hover:bg-blue-50/30 dark:border-gray-800 dark:hover:bg-blue-900/10 ${Number(row.id) % 2 === 1 ? 'bg-gray-50/50 dark:bg-gray-800/30' : ''}`}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-4 py-3">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Paginação */}
        {searched && response && response.total > 0 && (
          <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 text-sm dark:border-gray-700">
            <span className="text-gray-500 dark:text-gray-400">
              Mostrando {Math.min(pagination.pageIndex * pagination.pageSize + 1, response.total)} a{' '}
              {Math.min((pagination.pageIndex + 1) * pagination.pageSize, response.total)} de {response.total} registros
            </span>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
                <span className="text-xs">Resultados por página:</span>
                <select
                  value={pagination.pageSize}
                  onChange={(e) => setPagination({ pageIndex: 0, pageSize: Number(e.target.value) })}
                  className="rounded border border-gray-300 px-1.5 py-0.5 text-xs dark:border-gray-600 dark:bg-gray-800"
                >
                  {[10, 20, 50, 100].map((size) => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                  className="rounded border border-gray-300 px-3 py-1 text-sm disabled:opacity-40 dark:border-gray-600"
                >
                  Anterior
                </button>
                <span className="px-2 text-xs text-gray-500 dark:text-gray-400">
                  {pagination.pageIndex + 1} / {table.getPageCount()}
                </span>
                <button
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                  className="rounded border border-gray-300 px-3 py-1 text-sm disabled:opacity-40 dark:border-gray-600"
                >
                  Próxima
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal de ajuda */}
      {showHelp && <ProbeLogHelpModal onClose={() => setShowHelp(false)} />}
    </div>
  );
}
